/**
 * How Helm's messages to a customer greet them and name what they are about.
 * Kept free of Helm's path aliases so the API's tests can import it; the API's
 * templates greet by the same rule (src/communications/greeting.ts), and a
 * test holds the two together.
 */

/** Stand-ins Helm shows when it has no name; never worth greeting someone by. */
const PLACEHOLDERS = new Set([
  'customer',
  'client',
  'customer not linked',
  'unknown',
  'not set',
  'n/a',
  'there',
])

/**
 * A name worth greeting someone by: one line with letters in it. Never an
 * email address, a phone number or one of Helm's placeholders.
 */
export function greetableName(value: string | null | undefined): string | null {
  const name = value?.replace(/\s+/g, ' ').trim()
  if (!name || name.length > 80) return null
  if (name.includes('@') || !/\p{L}/u.test(name)) return null
  if (PLACEHOLDERS.has(name.toLowerCase())) return null
  return name
}

/** "Hi Grace Namata," or, with no name worth using, "Hello,". */
export function greetingLine(name: string | null | undefined) {
  const greetable = greetableName(name)
  return greetable ? `Hi ${greetable},` : 'Hello,'
}

/** Whom a message greets: the contact person, then the customer's name, then the company. */
export function customerGreetingName(
  customer:
    | {
        contact_person?: string | null
        name?: string | null
        company_name?: string | null
      }
    | null
    | undefined,
) {
  for (const candidate of [customer?.contact_person, customer?.name, customer?.company_name]) {
    const name = greetableName(candidate)
    if (name) return name
  }
  return null
}

/** The greeting line for a customer: "Hi Grace Namata," or "Hello,". */
export function customerGreeting(customer: Parameters<typeof customerGreetingName>[0]) {
  return greetingLine(customerGreetingName(customer))
}

/**
 * How a message to the customer names a shipment: Xtellar's reference and
 * what it is, "XTLCAIR202610160001 (Baby Food Processor)". Never a courier's
 * package number, which names one parcel inside it, not the shipment.
 */
export function shipmentReference(consignment: { code?: string | null; title?: string | null }) {
  const code = consignment.code?.trim()
  const title = consignment.title?.trim()
  if (code && title) return `${code} (${title})`
  return code || title || ''
}
